import React, { useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';

const HourlySalesModal = ({ open, data, loading, onClose }) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const isAzucarGlass = Boolean(data?.isAzucarGlass);
  const unitLabel = isAzucarGlass ? 'kgs' : (data?.unitLabel || 'unidades');
  const maxSelectableHours = Math.min(isAzucarGlass ? 24 : 12, rows.length);
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeHours, setRangeHours] = useState(0);
  const plotRef = useRef(null);

  useEffect(() => {
    if (!isAzucarGlass) {
      return;
    }
    setRangeStart(0);
    setRangeHours(maxSelectableHours || 0);
  }, [isAzucarGlass, maxSelectableHours, data?.articleName, open]);

  const safeStart = Math.max(0, Math.min(rangeStart, Math.max(0, rows.length - 1)));
  const safeHours = maxSelectableHours
    ? Math.max(1, Math.min(rangeHours || maxSelectableHours, maxSelectableHours, rows.length - safeStart))
    : 0;
  const selectedRows = useMemo(
    () => rows.slice(safeStart, safeStart + safeHours),
    [rows, safeStart, safeHours]
  );
  const selectedTotal = selectedRows.reduce(
    (sum, row) => sum + (Number(row.value) || 0),
    0
  );

  useEffect(() => {
    if (!open || !isAzucarGlass || !plotRef.current) {
      return undefined;
    }
    const node = plotRef.current;
    const labels = rows.map((row) => row.label);
    const values = rows.map((row) => Number(row.value) || 0);
    const colors = rows.map((_, index) => (
      index >= safeStart && index < safeStart + safeHours ? '#f97316' : '#e5e7eb'
    ));
    const plotData = [{
      x: labels,
      y: values,
      type: 'bar',
      marker: { color: colors },
      hovertemplate: `%{x}<br>%{y:.2f} ${unitLabel}<extra></extra>`
    }];
    const layout = {
      margin: { l: 40, r: 20, t: 10, b: 90 },
      height: 280,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      xaxis: { tickangle: -35, automargin: true },
      yaxis: { title: `Consumo (${unitLabel})`, rangemode: 'tozero' }
    };
    Plotly.react(node, plotData, layout, { displaylogo: false, responsive: true });
    return () => {
      if (node) {
        Plotly.purge(node);
      }
    };
  }, [open, isAzucarGlass, rows, safeStart, safeHours, unitLabel]);

  const totalDisplay = isAzucarGlass
    ? `${selectedTotal.toFixed(2)} ${unitLabel}`
    : data?.totalDisplay ?? data?.total ?? 0;
  const totalLabel = isAzucarGlass ? 'Total del rango seleccionado' : 'Total del pronóstico';
  const tableHeaderLabel = isAzucarGlass ? `Consumo (${unitLabel})` : 'Venta Pronosticada';

  if (!open) {
    return null;
  }

  if (loading) {
    return (
      <div className="modal" style={{ display: 'block' }}>
        <div className="modal-content ux-modal" style={{ textAlign: 'center', padding: '2rem' }}>
          <h3>Consultando datos horarios...</h3>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content ux-modal">
        <div className="ux-modal-header">
          <div className="ux-header-content">
            <h2>{data.title || 'Ventas por Hora'}</h2>
            <p className="ux-subtitle">{data.subtitle || 'Distribución horaria del pronóstico seleccionado'}</p>
          </div>
          <button className="ux-close-btn" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <div className="ux-modal-body">
          {isAzucarGlass ? (
            <div className="hourly-range-controls">
              <div className="hourly-range-field">
                <label className="form-label" htmlFor="hourly-range-start">Inicio</label>
                <select
                  id="hourly-range-start"
                  className="form-select"
                  value={safeStart}
                  onChange={(event) => setRangeStart(parseInt(event.target.value, 10) || 0)}
                  disabled={rows.length === 0}
                >
                  {rows.map((row, index) => (
                    <option key={row.label} value={index}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hourly-range-field">
                <label className="form-label" htmlFor="hourly-range-hours">Horas a considerar</label>
                <input
                  id="hourly-range-hours"
                  type="number"
                  min="1"
                  max={maxSelectableHours}
                  className="form-input"
                  value={safeHours || ''}
                  onChange={(event) => {
                    const next = parseInt(event.target.value, 10);
                    if (!Number.isFinite(next)) {
                      setRangeHours(1);
                      return;
                    }
                    const clamped = Math.max(1, Math.min(next, maxSelectableHours || 1));
                    setRangeHours(clamped);
                  }}
                  disabled={maxSelectableHours === 0}
                />
                <span className="input-hint">Máximo {maxSelectableHours || 0} horas</span>
              </div>
              <div className="hourly-range-total">
                <span className="range-total-label">Total seleccionado</span>
                <span className="range-total-value">{selectedTotal.toFixed(2)} {unitLabel}</span>
              </div>
            </div>
          ) : null}
          {isAzucarGlass ? (
            <div className="hourly-plotly-card">
              <div ref={plotRef} className="hourly-plotly-chart" />
            </div>
          ) : null}
          <div className="detailed-table-container">
            <table className="detailed-table react-table">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>{tableHeaderLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>{totalLabel}</strong></td>
                  <td style={{ textAlign: 'center' }}>
                    <strong>{totalDisplay}</strong>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}><hr /></td>
                </tr>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: 'center' }}>{row.displayValue ?? row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="ux-modal-footer">
          <button type="button" className="ux-btn ux-btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default HourlySalesModal;
